import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import {
  Box,
  Button,
  HStack,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import { MdArrowDropDown } from 'react-icons/md';
import { formatValueRef, parseValueRef, type TaskValuePart, type ValueRef } from './valueRef';
import type { ValueRefTask } from './valueRefTasks';

interface PartOption {
  part: TaskValuePart;
  field?: string;
  label: string;
  hint: string;
}

function partOptions(task: ValueRefTask): PartOption[] {
  const options: PartOption[] = [{ part: 'value', label: 'Full answer', hint: 'Everything that was answered' }];
  if (task.hasCodeLabel) {
    options.push({ part: 'code', label: 'Code', hint: "The option's ID" });
    options.push({ part: 'label', label: 'Label', hint: "The option's name" });
  }
  task.fields.forEach((field) => options.push({ part: 'attr', field: field.name, label: field.label, hint: field.name }));
  return options;
}

function partLabel(ref: Extract<ValueRef, { kind: 'task' }>, task: ValueRefTask): string {
  if (ref.part === 'attr') return task.fields.find((field) => field.name === ref.field)?.label ?? ref.field ?? '';
  return ref.part === 'value' ? 'Full answer' : ref.part === 'code' ? 'Code' : 'Label';
}

const optionId = (part: TaskValuePart, field?: string) => (part === 'attr' ? `attr-${field}` : part);

interface ValueRefPickerProps {
  value: string;
  onChange: (value: string) => void;
  tasks: ValueRefTask[];
  placeholder?: string;
  isInvalid?: boolean;
  testId: string;
}

type Mode = 'empty' | 'task' | 'now' | 'source_key' | 'launch' | 'text';

/**
 * Picks a value reference from a searchable list of tasks and their answer parts. It
 * writes the same strings authors used to type, and opens a value it can't show as a
 * task in "Type a value" mode, unchanged.
 */
export function ValueRefPicker({ value, onChange, tasks, placeholder = 'Choose a value', isInvalid, testId }: ValueRefPickerProps) {
  // A plain dropdown under the field rather than a Popover: no animation or repositioning,
  // so the list never moves while it is being clicked.
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [search, setSearch] = useState('');
  // Set while the author edits text or a launch row, so the input stays up even when empty.
  // Tied to the value it was set for, so a row that shifts under this picker (an entry
  // above it removed) shows its own value rather than inheriting the mode.
  const [forced, setForced] = useState<{ mode: 'text' | 'launch'; value: string } | null>(null);
  const forcedMode = forced && forced.value === value ? forced.mode : null;
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const ref = parseValueRef(value);
  const task = ref.kind === 'task' ? tasks.find((candidate) => candidate.row === ref.row) : undefined;
  const mode: Mode =
    forcedMode ??
    (value === '' ? 'empty' : ref.kind === 'task' ? (task ? 'task' : 'text') : ref.kind);

  let problem: { level: 'error' | 'warning'; text: string } | null = null;
  if (ref.kind === 'text' && ref.error) problem = { level: 'error', text: ref.error };
  else if (ref.kind === 'task' && !task) problem = { level: 'error', text: `There is no task ${ref.row}. Choose another value.` };
  else if (ref.kind === 'task' && task?.unavailableReason) problem = { level: 'warning', text: `"${task.name}" is answered after this task, so it would be empty.` };
  else if (ref.kind === 'task' && task && ref.part === 'attr' && task.fields.length > 0 && !task.fields.some((field) => field.name === ref.field))
    problem = { level: 'warning', text: `"${task.name}" has no field "${ref.field}". Its fields: ${task.fields.map((field) => field.name).join(', ')}.` };
  else if (ref.kind === 'text' && /^(task_[^_\s]+_(value|attr)|attr_\d)/.test(ref.text))
    problem = { level: 'warning', text: 'This looks like a reference without its { } braces, so it is saved as plain text.' };

  const query = search.trim().toLowerCase();
  const groups = useMemo(
    () =>
      tasks
        .map((candidate) => ({
          task: candidate,
          options: partOptions(candidate).filter(
            (option) =>
              !query ||
              candidate.name.toLowerCase().includes(query) ||
              option.label.toLowerCase().includes(query) ||
              option.hint.toLowerCase().includes(query),
          ),
        }))
        .filter((group) => group.options.length > 0),
    [tasks, query],
  );
  const others: { id: string; label: string; hint: string; choose: () => void }[] = [
    { id: 'now', label: 'Current time', hint: 'When this runs', choose: () => pick({ kind: 'now' }) },
    { id: 'source_key', label: 'Source key', hint: 'The scanned item or card', choose: () => pick({ kind: 'source_key' }) },
    { id: 'launch', label: 'Value passed in at launch', hint: 'From the workflow that started this one', choose: () => chooseForced('launch') },
    { id: 'text', label: 'Type a value or reference', hint: 'Fixed text, or a reference by hand', choose: () => chooseForced('text') },
    ...(value !== '' || forcedMode ? [{ id: 'clear', label: 'Clear', hint: 'Leave this empty', choose: clear }] : []),
  ].filter((option) => !query || option.label.toLowerCase().includes(query) || option.hint.toLowerCase().includes(query));

  function close() {
    setSearch('');
    setIsOpen(false);
  }

  useEffect(() => {
    if (!isOpen) return;
    searchRef.current?.focus();
    const onPointerDown = (event: PointerEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return;
      setSearch('');
      setIsOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [isOpen]);

  function pick(next: ValueRef) {
    setForced(null);
    onChange(formatValueRef(next));
    close();
  }

  function clear() {
    setForced(null);
    onChange('');
    close();
  }

  function chooseForced(next: 'text' | 'launch') {
    const nextValue = next === 'launch' && ref.kind !== 'launch' ? '' : value;
    setForced({ mode: next, value: nextValue });
    if (nextValue !== value) onChange(nextValue);
    close();
  }

  function onListKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const options = Array.from(listRef.current?.querySelectorAll<HTMLButtonElement>('button[data-option]') ?? []);
    const at = options.indexOf(document.activeElement as HTMLButtonElement);
    const next = event.key === 'ArrowDown' ? Math.min(at + 1, options.length - 1) : at - 1;
    if (next < 0) searchRef.current?.focus();
    else options[next]?.focus();
    event.preventDefault();
  }

  const otherLabels: Record<string, string> = {
    now: 'Current time',
    source_key: 'Source key',
    launch: 'Value passed in at launch',
    text: 'Type a value or reference',
  };

  return (
    <VStack align="stretch" spacing={1} flex={1} minW={0}>
      <Box
        ref={containerRef}
        position="relative"
        minW={0}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && isOpen) {
            event.stopPropagation();
            close();
            triggerRef.current?.focus();
          }
        }}
      >
        <Button
          ref={triggerRef}
          onClick={() => (isOpen ? close() : setIsOpen(true))}
          w="100%"
          aria-haspopup="true"
          aria-expanded={isOpen}
          size="sm"
          variant="outline"
          justifyContent="space-between"
          fontWeight="normal"
          borderColor={isInvalid || problem?.level === 'error' ? 'red.500' : 'border-default'}
          bg="bg-surface"
          rightIcon={<MdArrowDropDown />}
          data-testid={testId}
          data-value={value}
          minW={0}
        >
          <Box minW={0} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" textAlign="left">
            {mode === 'task' && ref.kind === 'task' && task ? (
              <HStack spacing={2} minW={0}>
                <Text as="span" fontSize="xs" fontWeight="semibold" color="text-muted">{task.row}</Text>
                <Text as="span" isTruncated>{task.name}</Text>
                <Text as="span" color="brand.500" fontWeight="medium" isTruncated>› {partLabel(ref, task)}</Text>
              </HStack>
            ) : mode === 'empty' ? (
              <Text as="span" color="text-muted">{placeholder}</Text>
            ) : (
              <Text as="span">{otherLabels[mode]}</Text>
            )}
          </Box>
        </Button>
        {isOpen && (
          <Box
            position="absolute"
            top="calc(100% + 4px)"
            // Anchored to the field's right edge so it opens leftward: pickers sit in the
            // right-hand column of their rows, and a list running past it scrolls the panel.
            right={0}
            zIndex="dropdown"
            w="max(100%, 20rem)"
            maxW="calc(100vw - 32px)"
            p={2}
            bg="bg-surface"
            borderWidth="1px"
            borderColor="border-default"
            rounded="md"
            shadow="lg"
            data-testid={`${testId}-menu`}
          >
            <Input
              ref={searchRef}
              size="sm"
              placeholder="Search tasks or fields"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown') {
                  listRef.current?.querySelector<HTMLButtonElement>('button[data-option]')?.focus();
                  event.preventDefault();
                }
              }}
              mb={2}
              data-testid={`${testId}-search`}
            />
            <Box ref={listRef} maxH="300px" overflowY="auto" onKeyDown={onListKeyDown}>
              {groups.map(({ task: group, options }) => (
                <Box key={group.row} mb={1}>
                  <HStack px={2} pt={2} pb={1} spacing={2} color={group.unavailableReason ? 'text-muted' : 'text-primary'}>
                    <Text fontSize="xs" fontWeight="semibold">{group.row}</Text>
                    <Text fontSize="sm" fontWeight="semibold" isTruncated>{group.name}</Text>
                    <Text fontSize="xs" color="text-muted">{group.typeLabel}</Text>
                  </HStack>
                  {group.unavailableReason ? (
                    <Text fontSize="xs" color="text-muted" px={2} pl={7} pb={1}>{group.unavailableReason}</Text>
                  ) : (
                    options.map((option) => {
                      const next: ValueRef = { kind: 'task', row: group.row, part: option.part, field: option.field };
                      return (
                        <Button
                          key={optionId(option.part, option.field)}
                          data-option
                          variant="ghost"
                          size="sm"
                          w="100%"
                          justifyContent="space-between"
                          fontWeight="normal"
                          pl={7}
                          isActive={formatValueRef(next) === value}
                          onClick={() => pick(next)}
                          data-testid={`${testId}-option-${group.row}-${optionId(option.part, option.field)}`}
                        >
                          <Text as="span">{option.label}</Text>
                          <Text as="span" fontSize="xs" color="text-muted" isTruncated ml={3}>{option.hint}</Text>
                        </Button>
                      );
                    })
                  )}
                </Box>
              ))}
              {others.length > 0 && (
                <Box>
                  <Text px={2} pt={2} pb={1} fontSize="sm" fontWeight="semibold">Other</Text>
                  {others.map((option) => (
                    <Button
                      key={option.id}
                      data-option
                      variant="ghost"
                      size="sm"
                      w="100%"
                      justifyContent="space-between"
                      fontWeight="normal"
                      onClick={option.choose}
                      data-testid={`${testId}-option-${option.id}`}
                    >
                      <Text as="span">{option.label}</Text>
                      <Text as="span" fontSize="xs" color="text-muted" isTruncated ml={3}>{option.hint}</Text>
                    </Button>
                  ))}
                </Box>
              )}
              {groups.length === 0 && others.length === 0 && (
                <Text fontSize="sm" color="text-muted" px={2} py={2}>Nothing matches “{search}”.</Text>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {mode === 'text' && (
        <Input
          size="sm"
          placeholder="Fixed text, or a reference such as {task_1_value}"
          value={value}
          onChange={(event) => {
            setForced({ mode: 'text', value: event.target.value });
            onChange(event.target.value);
          }}
          fontFamily={ref.kind === 'text' && !ref.text.startsWith('{') ? undefined : 'mono'}
          bg="bg-surface"
          borderColor="border-default"
          data-testid={`${testId}-input`}
        />
      )}
      {mode === 'launch' && (
        <Input
          size="sm"
          placeholder="Name of the value passed in, e.g. 2 or store_code"
          value={ref.kind === 'launch' ? ref.key : ''}
          onChange={(event) => {
            const key = event.target.value.trim();
            const nextValue = key ? formatValueRef({ kind: 'launch', key }) : '';
            setForced({ mode: 'launch', value: nextValue });
            onChange(nextValue);
          }}
          bg="bg-surface"
          borderColor="border-default"
          data-testid={`${testId}-launch`}
        />
      )}
      {problem && (
        <Text fontSize="xs" color={problem.level === 'error' ? 'red.500' : 'orange.500'} data-testid={`${testId}-problem`}>
          {problem.text}
        </Text>
      )}
    </VStack>
  );
}
